import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteNotifications, flushNotificationError, setNotification } from 'store/notifications/actions'
import classNames from 'classnames'
import styles from './styles/Notification.css'
import Tooltip from 'components/misc/Tooltip'
import * as analytics from 'lib/analytics'
import { toggleModal, MODAL_FROM_CONTEXTS } from 'store/ui/actions'
import Checkbox from '../forms/Checkbox'
import { FadeVisual } from './Fade'
import CircleSpinner from './CircleSpinner'
import toast from 'react-hot-toast'
import { userIsAtLeast } from 'lib/user'
import { PLANS } from '../../../server/lib/constants'
import { setMode } from '../../store/login-modal/actions'

const Notification = ({ source, name, category, className = '', checkbox = false, checkBoxDisabled = false }) => {
  const dispatch = useDispatch()
  const notifications = useSelector(({ notifications }) => notifications?.notifications || [])
  const loading = useSelector(({ notifications }) => notifications?.loading)
  const error = useSelector(({ notifications }) => notifications?.error)
  const user = useSelector(({ ui }) => ui?.user)
  const fetchingUser = useSelector(({ ui }) => ui?.fetchingUser)

  const findNotification = ({ source: notificationSource, category: notificationCategory }) => `${notificationSource}` === `${source}` && `${notificationCategory}` === `${category}`
  const notificationSet = notifications.filter(findNotification).length !== 0 && notifications.filter(findNotification)[0]

  const handleClick = () => {
    if (!user?.email) {
      dispatch(setMode('register'))
      dispatch(toggleModal('login', true, MODAL_FROM_CONTEXTS.NOTIFICATION))
      return
    }

    if (!userIsAtLeast(user, PLANS.premium)) {
      dispatch(toggleModal('upgrade', true, MODAL_FROM_CONTEXTS.NOTIFICATION))
      return
    }

    if (notificationSet) {
      dispatch(deleteNotifications([notificationSet.id]))
      analytics.event({
        category: 'User',
        action: 'Remove notification',
        label: name
      })
      return
    }
    dispatch(
      setNotification(
        {
          name,
          source,
          category,
          email: user?.email
        }))
    analytics.event({
      category: 'User',
      action: 'Set notification',
      label: name
    })
  }

  const entityCopy = user?.email === source && (category === 'author' || category === 'researcher')
    ? (
        'your publications'
      )
    : (
      `this ${category === 'paper' ? 'publication' : category}`
      )
  const tooltipCopy = notificationSet
    ? (
    `An email alert is set for when new citations are made to ${entityCopy}. Click here to manage your alerts.`
      )
    : (
      `Set an email alert for when new citations are made to ${entityCopy}.`
      )

  useEffect(() => {
    if (error?.error && error?.source === source && error?.category === category) {
      toast.error((t) => (
        <div className={styles.toastLayout}>
          <div>{error.error}</div>
          <div
            className={styles.closer} onClick={() => {
              dispatch(flushNotificationError())
              toast.dismiss(t.id)
            }}
          ><i className='fa fa-lg fa-times' />
          </div>
        </div>
      ), {
        duration: 100000,
        style: {
          maxWidth: '60rem',
          paddingLeft: '1.25rem' // To account for react-hot-toast inner style
        }
      })
    }
  }, [error])

  return (
    <FadeVisual show={!fetchingUser} className={`${styles.notificationContainer} ${className}`}>
      {checkbox
        ? (
            loading
              ? (
                <span className={styles.setNotification}>
                  <CircleSpinner size={20} />
                </span>
                )
              : (
                <Checkbox disabled={checkBoxDisabled} checked={!!notificationSet} onChange={handleClick} />
                )
          )
        : (
          <div role='button' className={styles.notification} onClick={handleClick} tabIndex={0}>
            <div
              className={classNames(styles.bellIcon, {
                [styles.notificationSet]: !!notificationSet
              })}
            />
            {(loading || fetchingUser)
              ? (
                <span className={styles.setNotification}>
                  <CircleSpinner size={20} />
                </span>
                )
              : (
                <Tooltip
                  placement='bottom'
                  content={tooltipCopy}
                  refClassName={styles.tooltip}
                >
                  <span className={styles.setNotification}>
                    {user?.email
                      ? (
                          notificationSet ? 'Remove alert' : 'Set alert'
                        )
                      : (
                          'Sign up to set email alerts'
                        )}
                  </span>
                </Tooltip>
                )}
          </div>

          )}
    </FadeVisual>
  )
}

export default Notification
